//ShippingPreparationModal.tsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  IconButton,
  Chip,
  Alert,
  AlertTitle,
  CircularProgress,
  Paper,
  Divider
} from '@mui/material';
import {
  LocalShipping as ShippingIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Store as StoreIcon,
  LocationOn as LocationIcon,
  Print as PrintIcon,
  Add as AddIcon,
  Remove as RemoveIcon
} from '@mui/icons-material';
import api from '../services/api';
import axios from 'axios';

interface ShippingPreparationModalProps {
  open: boolean;
  onClose: () => void;
  order: any;
  enqueueSnackbar: (message: string, options?: any) => void;
  onShippingPrepared?: (preparedData: any) => void;
}

interface LogisticsInfo {
  cvs_company: string | null;
  cvs_store_id: string;
  store_name: string | null;
  store_address: string | null;
  store_city: string | null;
  store_zip: string | null;
  logistics_subtype: string;
}

// Helper function to format Chinese name
const formatChineseName = (firstName: string, lastName: string): string => {
  // Check if name contains Chinese characters
  const chinesePattern = /[\u4e00-\u9fff]/;

  if (chinesePattern.test(firstName) || chinesePattern.test(lastName)) {
    // For Chinese names, format as lastName + firstName (no space)
    return `${lastName || ''}${firstName || ''}`.trim();
  } else {
    // For non-Chinese names, format as firstName + lastName (with space)
    return `${firstName || ''} ${lastName || ''}`.trim();
  }
};

// Helper function to format Taiwan phone number
const formatTaiwanPhone = (phone: string): string => {
  if (!phone) return '';

  // Remove any non-numeric characters
  let cleanPhone = phone.replace(/\D/g, '');

  // If phone starts with Taiwan country code (886), replace with 0
  if (cleanPhone.startsWith('886')) {
    cleanPhone = '0' + cleanPhone.substring(3);
  }

  // If phone doesn't start with 0, add it (for 9-digit numbers)
  if (!cleanPhone.startsWith('0') && cleanPhone.length === 9) {
    cleanPhone = '0' + cleanPhone;
  }

  return cleanPhone;
};

// Helper function to sanitize goods name (max 25 chars, no special chars)
const sanitizeGoodsName = (goodsName: string): string => {
  if (!goodsName) return 'Goods';

  // Remove emoji and special characters
  const cleaned = goodsName.replace(/[^\w\s\u4e00-\u9fff,.]/g, '');

  // Limit to 25 characters
  return cleaned.length > 25 ? `${cleaned.substring(0, 22)}...` : cleaned;
};

// Generate a description of items with proper sanitization
const generateItemsDescription = (order: any): string => {
  const lineItems = order?.original?.line_items || [];

  if (lineItems.length === 0) return 'Goods';

  // Take up to 3 items to avoid excessive length
  const itemDescriptions = lineItems.slice(0, 3).map((item: any) =>
    `${item.title?.split(' ')[0] || ''} x ${item.quantity}`
  );

  let description = itemDescriptions.join(', ');

  // Add a count of remaining items if there are more
  if (lineItems.length > 3) {
    description += ` + ${lineItems.length - 3} more`;
  }

  // Sanitize and limit length
  return sanitizeGoodsName(description);
};

// Parse convenience store details from shipping address
// Format: "台南市仁德區中洲里中洲路661號663號1樓(超商), 942548 中洲門市(UNIMARTC2C), 台南市, Taiwan, 717"
const parseStoreFromAddress = (shippingAddress: string): {
  cvs_store_id: string;
  store_name: string;
  logistics_subtype: string;
} => {
  const defaultResult = {
    cvs_store_id: '',
    store_name: '',
    logistics_subtype: 'UNIMARTC2C'
  };

  if (!shippingAddress) return defaultResult;

  try {
    // Split by comma to get address parts
    const parts = shippingAddress.split(',').map(p => p.trim());

    // The store info is usually in the second part
    // Format: "942548 中洲門市(UNIMARTC2C)"
    if (parts.length >= 2) {
      const storeInfo = parts[1];

      // Extract store ID (6 digits at the start)
      const storeIdMatch = storeInfo.match(/^(\d{6})/);
      const cvs_store_id = storeIdMatch ? storeIdMatch[1] : '';

      // Extract store name (between store ID and parentheses)
      const storeNameMatch = storeInfo.match(/^\d{6}\s+([^(]+)/);
      const store_name = storeNameMatch ? storeNameMatch[1].trim() : '';

      // Extract logistics type (inside parentheses)
      const logisticsMatch = storeInfo.match(/\(([^)]+)\)/);
      const logistics_subtype = logisticsMatch ? logisticsMatch[1] : 'UNIMARTC2C';

      return {
        cvs_store_id,
        store_name,
        logistics_subtype
      };
    }
  } catch (error) {
    console.error('Failed to parse store info from address:', error);
  }

  return defaultResult;
};

const calculateOrderTotal = (order: any): number => {
  let totalAmount = 0;

  console.log('=== ORDER TOTAL CALCULATION DEBUG ===');
  console.log('Order original data:', order?.original);
  console.log('current_total_price:', order?.original?.current_total_price);
  console.log('total_price:', order?.original?.total_price);
  console.log('subtotal_price:', order?.original?.subtotal_price);
  console.log('total_discounts:', order?.original?.total_discounts);
  console.log('total_price_set:', order?.original?.total_price_set);

  // Priority 1: Use current_total_price (includes all discounts)
  if (order?.original?.current_total_price) {
    totalAmount = Math.floor(parseFloat(order.original.current_total_price) || 0);
    console.log('✅ Using current_total_price (with discounts):', totalAmount);
    return totalAmount;
  }

  // Priority 2: Use total_price_set if available (newer Shopify format)
  if (order?.original?.total_price_set?.shop_money?.amount) {
    totalAmount = Math.floor(parseFloat(order.original.total_price_set.shop_money.amount) || 0);
    console.log('✅ Using total_price_set amount:', totalAmount);
    return totalAmount;
  }

  // Priority 3: Calculate from subtotal minus total discounts
  if (order?.original?.subtotal_price && order?.original?.total_discounts) {
    const subtotal = parseFloat(order.original.subtotal_price) || 0;
    const discounts = parseFloat(order.original.total_discounts) || 0;
    totalAmount = Math.floor(subtotal - discounts);
    console.log('✅ Calculated from subtotal minus discounts:', subtotal, '-', discounts, '=', totalAmount);
    return totalAmount;
  }

  // Priority 4: Use total_price as fallback (may not include some discounts)
  if (order?.original?.total_price) {
    totalAmount = Math.floor(parseFloat(order.original.total_price) || 0);
    console.log('⚠️ Using total_price as fallback (may not include discounts):', totalAmount);
    return totalAmount;
  }

  // Priority 5: Calculate from line items with their discount_allocations
  if (order?.original?.line_items) {
    totalAmount = order.original.line_items.reduce((sum: number, item: any) => {
      const itemPrice = parseFloat(item.price || 0);
      const quantity = parseInt(item.quantity || 0);
      let itemTotal = itemPrice * quantity;

      // Subtract line item discounts if available
      if (item.discount_allocations && Array.isArray(item.discount_allocations)) {
        const itemDiscounts = item.discount_allocations.reduce((discountSum: number, discount: any) => {
          return discountSum + (parseFloat(discount.amount || 0));
        }, 0);
        itemTotal -= itemDiscounts;
        console.log(`Item ${item.title}: ${itemPrice} x ${quantity} - ${itemDiscounts} discount = ${itemTotal}`);
      }

      return sum + itemTotal;
    }, 0);

    totalAmount = Math.floor(totalAmount);
    console.log('✅ Calculated from line items with discounts:', totalAmount);
    return totalAmount;
  }

  console.warn('❌ Could not calculate order total, using 0');
  console.log('=====================================');
  return 0;
};

// Main component
const ShippingPreparationModal: React.FC<ShippingPreparationModalProps> = ({
  open,
  onClose,
  order,
  enqueueSnackbar,
  onShippingPrepared
}) => {
  console.log('Order received in modal:', order);

  // Loading state for API calls
  const [fetchingInfo, setFetchingInfo] = useState(false);
  const [printing, setPrinting] = useState(false);

  // State for logistics information from API
  const [logisticsInfo, setLogisticsInfo] = useState<LogisticsInfo>({
    cvs_company: null,
    cvs_store_id: '',
    store_name: null,
    store_address: null,
    store_city: null,
    store_zip: null,
    logistics_subtype: 'UNIMARTC2C' // Default to 7-ELEVEN
  });

  // State to track if shipping has been prepared for this order
  const [isShippingPrepared, setIsShippingPrepared] = useState(false);
  const [shippingInfo, setShippingInfo] = useState<any>(null);

  // Initial state setup with improved extraction
  const [shippingDetails, setShippingDetails] = useState(() => {
    // Extract customer details
    const customer = order?.original?.customer || {};
    const shippingAddress = order?.original?.shipping_address || {};

    // Get formatted customer name
    const customerName = formatChineseName(
      shippingAddress.first_name || '',
      shippingAddress.last_name || ''
    );

    // Format customer phone
    const customerPhone = formatTaiwanPhone(
      shippingAddress.phone || customer.phone || ''
    );

    // Generate items description
    const itemsDescription = generateItemsDescription(order);

    // Calculate total amount (convert to integer)

    const totalAmount = calculateOrderTotal(order);


    return {
      // Customer Details
      customerName,
      customerPhone,
      customerEmail: customer.email || '',

      // Sender Details (fixed values)
      senderName: '邱泰滕',
      senderPhone: '0988528467',
      senderAddress: '',

      // Order Details
      orderNumber: order?.orderNumber || '',
      items: order?.original?.line_items || [],
      itemsDescription: itemsDescription,
      totalAmount: totalAmount,

      // Logistics Details - will be updated by API
      logistics_subtype: 'UNIMARTC2C',
      cvs_store_id: '',
      store_name: '',
      tracking_number: '',

      // Payment collection
      isCollection: order?.original?.financial_status === 'pending' ? 'Y' : 'N'
    };
  });

  // Check if shipping is already prepared for this order
  useEffect(() => {
    const checkShippingStatus = async () => {
      if (order?.orderNumber) {
        try {
          const response = await api.get(`/api/shopify/shipping/${order.orderNumber}`);
          // Only treat as "shipping prepared" when a REAL ECPay logistics id was
          // assigned. Web/storefront orders insert a placeholder row at checkout
          // (store selection only, ecpay_logistics_id = null, status PENDING); that
          // must NOT hide the 建立物流單 button or the shipment can never be created.
          if (response.data.success && response.data.logistics && response.data.logistics.logisticsId) {
            setIsShippingPrepared(true);
            setShippingInfo(response.data.logistics);
          }
        } catch (error) {
          // Shipping not prepared yet, which is fine
          console.log('No shipping prepared yet for this order');
        }
      }
    };

    checkShippingStatus();
  }, [order?.orderNumber]);

  // Parse logistics info from shipping address or direct fields (storefront orders)
  useEffect(() => {
    // Storefront orders pass store_id/store_name/logistics_sub_type directly
    if (order?.store_id) {
      const subtype = order.logistics_sub_type || 'UNIMARTC2C';
      setLogisticsInfo({
        cvs_company: subtype === 'FAMIC2C' ? 'FamilyMart' : '7-ELEVEN',
        cvs_store_id: order.store_id,
        store_name: order.store_name || '',
        store_address: order.store_address || null,
        store_city: null,
        store_zip: null,
        logistics_subtype: subtype,
      });
      setShippingDetails(prev => ({
        ...prev,
        cvs_store_id: order.store_id,
        store_name: order.store_name || prev.store_name,
        logistics_subtype: subtype,
      }));
      return;
    }

    if (order?.shippingAddress) {
      try {
        setFetchingInfo(true);

        // Parse store info from the formatted shipping address
        const parsedStoreInfo = parseStoreFromAddress(order.shippingAddress);

        console.log('Parsed store info from address:', parsedStoreInfo);
        console.log('Original shipping address:', order.shippingAddress);

        // Update logistics info state
        setLogisticsInfo({
          cvs_company: parsedStoreInfo.logistics_subtype === 'UNIMARTC2C' ? '7-ELEVEN' : 'FamilyMart',
          cvs_store_id: parsedStoreInfo.cvs_store_id,
          store_name: parsedStoreInfo.store_name,
          store_address: null,
          store_city: null,
          store_zip: null,
          logistics_subtype: parsedStoreInfo.logistics_subtype
        });

        // Also update shipping details with this information
        setShippingDetails(prev => ({
          ...prev,
          cvs_store_id: parsedStoreInfo.cvs_store_id || prev.cvs_store_id,
          store_name: parsedStoreInfo.store_name || prev.store_name,
          logistics_subtype: parsedStoreInfo.logistics_subtype || prev.logistics_subtype
        }));

      } catch (error) {
        console.error('Failed to parse logistics info from address:', error);
        enqueueSnackbar('Failed to parse store information from address', { variant: 'warning' });
      } finally {
        setFetchingInfo(false);
      }
    }
  }, [order?.shippingAddress, enqueueSnackbar]);

  // Track loading state
  const [loading, setLoading] = useState(false);

  // Track per-item quantity adjustments (for 斷貨 out-of-stock items)
  const [itemQtyAdjustments, setItemQtyAdjustments] = useState<{ [key: string]: number }>({});

  // Storefront orders carry per-line paid shares from the receiving page.
  const usesPaidShares: boolean = order?.platform === 'storefront'
    && (order?.items || []).some((it: any) => it.paidLineTotal !== undefined);
  const paidShareOf = (item: any, qty: number): number => {
    const origQty = parseInt(item.quantity || 0);
    return origQty > 0 ? Math.round(parseFloat(item.paidLineTotal || 0) * qty / origQty) : 0;
  };
  // Paid value of the units staff removed with the −/+ buttons (斷貨 lines are
  // already out of the base amount, so they never count here).
  const qtyRemovedAmount: number = useMemo(() => {
    if (!usesPaidShares) return 0;
    return (order?.items || []).reduce((sum: number, item: any, index: number) => {
      if (item.outOfStock) return sum;
      const key = item.id || String(index);
      const origQty = parseInt(item.quantity || 0);
      const adj = itemQtyAdjustments[key];
      if (adj === undefined || adj >= origQty) return sum;
      return sum + paidShareOf(item, origQty - adj);
    }, 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usesPaidShares, order, itemQtyAdjustments]);

  // Recalculate totalAmount when item adjustments change
  useEffect(() => {
    const items = order?.items || [];
    if (Object.keys(itemQtyAdjustments).length === 0) return;

    // Storefront: the receiving page already handed us the exact amount to collect
    // (paid total − 購物金 − 斷貨) and each line's paid share (its slice of the
    // order-level promo). Removing units only subtracts their paid share, so a
    // fixed 現折250/500 stays spread over the whole order instead of being dumped
    // on whatever is left (that used to drive COD negative), and shipping + 購物金
    // are never dropped.
    if (usesPaidShares) {
      setShippingDetails(prev => ({ ...prev, totalAmount: Math.max(0, calculateOrderTotal(order) - qtyRemovedAmount) }));
      return;
    }

    let newTotal = 0;
    items.forEach((item: any, index: number) => {
      if (item.outOfStock) return; // 斷貨 items stay excluded even after manual qty edits
      const itemKey = item.id || String(index);
      const origQty = parseInt(item.quantity || 0);
      const adjustedQty = itemQtyAdjustments[itemKey] !== undefined ? itemQtyAdjustments[itemKey] : origQty;
      const unitPrice = parseFloat(item.price || 0);
      const itemDiscount = parseFloat(item.total_discount || 0);
      const discountPerUnit = origQty > 0 ? itemDiscount / origQty : 0;
      newTotal += (unitPrice * adjustedQty) - (discountPerUnit * adjustedQty);
    });

    // Apply order-level promo discount proportionally (in-stock items only)
    const promoDiscount = parseFloat(order?.discountAmount || 0);
    if (promoDiscount > 0) {
      const origTotal = items.reduce((s: number, it: any) => it.outOfStock ? s : s + parseFloat(it.price || 0) * parseInt(it.quantity || 0), 0);
      const promoRatio = origTotal > 0 ? promoDiscount / origTotal : 0;
      newTotal = newTotal * (1 - promoRatio);
    }

    const rounded = Math.floor(newTotal);
    setShippingDetails(prev => ({ ...prev, totalAmount: rounded }));
  }, [itemQtyAdjustments, order, usesPaidShares, qtyRemovedAmount]);

  // Determine if order is paid
  const isPaid = useMemo(() => {
    const paidStatuses = ['paid', 'partially_paid'];
    return paidStatuses.includes(order?.original?.financial_status);
  }, [order]);

  // 斷貨 (out-of-stock) summary — passed from the receiving page (ReceivingQueuePage
  // handleShip). OOS items are excluded from the collected goods amount; surface
  // that here so the total visibly reconciles instead of looking "wrong".
  const oosCount: number = order?.oosCount ?? (order?.items || []).filter((i: any) => i.outOfStock).length;
  const oosDeduction: number = Math.round(parseFloat(order?.oosDeduction ?? 0)) || 0;
  const originalGoods: number = Math.round(parseFloat(order?.originalGoods ?? 0)) || 0;
  const storeCreditUsed: number = Math.round(parseFloat(order?.storeCreditUsed ?? 0)) || 0;
  const hasOOS: boolean = oosCount > 0;

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setShippingDetails(prev => ({
      ...prev,
      [name]: name === 'totalAmount' ? (value === '' ? 0 : parseInt(value) || 0) : value
    }));
  };

  // Handle select changes
  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setShippingDetails(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle printing shipping document
  // Handle printing shipping document
  // Handle printing shipping document
  // Improved print function with better error handling and more thorough logging
  // TypeScript-compatible print function with proper error type checking
  // Updated print function to use ECPay direct print endpoint
  const handlePrintShippingDocument = async () => {
    try {
      setPrinting(true);

      console.log("Print document requested for order:", order.orderNumber);
      console.log("Shipping info available:", isShippingPrepared, shippingInfo);

      // Use the working ECPay print endpoint that works in SalesHistoryTab
      if (isShippingPrepared && shippingInfo && shippingInfo.logisticsId) {
        console.log("Using logistics info for printing:", shippingInfo);

        try {
          // ✅ USE THE SAME WORKING ENDPOINT AS SALESHISTORYTAB
          const response = await api.post(`/api/ecpay/logistics/print/${order.orderNumber}`, {
            platform: 'shopify', // Mark as Shopify instead of 'web'
            logistics_sub_type: shippingDetails.logistics_subtype
          });

          console.log("Print response received, opening new window");

          // Create blob and open in new window
          const blob = new Blob([response.data], { type: 'text/html' });
          const url = URL.createObjectURL(blob);

          window.open(url, '_blank');

          // Clean up
          setTimeout(() => URL.revokeObjectURL(url), 100);

          enqueueSnackbar('出貨單已在新視窗中開啟', { variant: 'success' });
          return;
        } catch (directPrintError) {
          console.error("Direct print with logistics ID failed:", directPrintError);
          // Don't throw - continue to fallback method
        }
      }

      // Fallback: Use order-based query printing (same as your SalesHistoryTab pattern)
      console.log("Using order-based fallback printing for order:", order.orderNumber);

      const response = await api.post(`/api/ecpay/logistics/print/${order.orderNumber}`, {
        platform: 'shopify'
      });

      console.log("Fallback print response received");

      const blob = new Blob([response.data], { type: 'text/html' });
      const url = URL.createObjectURL(blob);

      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 100);

      enqueueSnackbar('出貨單已在新視窗中開啟', { variant: 'success' });

    } catch (error: unknown) {
      console.error('Print error details:', error);

      let errorMessage = 'Failed to print shipping document';

      if (axios.isAxiosError(error)) {
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setPrinting(false);
    }
  };

  // Handle shipping submission
  // Modified handleSubmitShipping function to prevent duplicate API calls
  const handleSubmitShipping = async () => {
    // Prevent re-submission if already in progress
    if (loading) {
      console.log('Submission already in progress, ignoring additional click');
      return;
    }

    setLoading(true);

    try {
      // Validate required fields
      if (!shippingDetails.senderName || !shippingDetails.senderPhone) {
        enqueueSnackbar('Sender name and phone are required', { variant: 'error' });
        return;
      }

      if (!shippingDetails.customerName || !shippingDetails.customerPhone) {
        enqueueSnackbar('Customer name and phone are required', { variant: 'error' });
        return;
      }

      if (!shippingDetails.cvs_store_id) {
        enqueueSnackbar('Store ID is required', { variant: 'error' });
        return;
      }

      // Validate goods amount
      if (!shippingDetails.totalAmount || shippingDetails.totalAmount <= 0) {
        enqueueSnackbar('Goods amount must be greater than 0', { variant: 'error' });
        return;
      }

      console.log(`Creating logistics for Shopify order ${order.orderNumber}`);

      console.log('=== DEBUGGING SHOPIFY LOGISTICS CREATION ===');
      console.log('Order financial status:', order?.original?.financial_status);
      console.log('Calculated isCollection:', shippingDetails.isCollection);
      console.log('Goods amount being sent:', shippingDetails.totalAmount);
      console.log('Customer name:', shippingDetails.customerName);
      console.log('Customer phone:', shippingDetails.customerPhone);
      console.log('Store ID:', shippingDetails.cvs_store_id);
      console.log('Logistics sub type:', shippingDetails.logistics_subtype);
      console.log('Goods description:', shippingDetails.itemsDescription);
      console.log('============================================');

      // ✅ USE THE NEW SHOPIFY-SPECIFIC ENDPOINT
      const logisticsResponse = await api.post(`/api/ecpay/logistics/create-for-shopify`, {
        orderId: String(order.orderNumber),
        storeId: shippingDetails.cvs_store_id,
        customerName: shippingDetails.customerName,
        customerPhone: shippingDetails.customerPhone,
        customerEmail: shippingDetails.customerEmail,
        logisticsSubType: shippingDetails.logistics_subtype,
        goodsAmount: shippingDetails.totalAmount, // ✅ Required parameter from frontend
        storeName: shippingDetails.store_name,
        isCollection: shippingDetails.isCollection, // ✅ Required parameter from frontend
        goodsName: shippingDetails.itemsDescription, // ✅ Add goods description
        source: 'shopify' // Mark the source for tracking
      });

      console.log('Shopify logistics response:', logisticsResponse.data);

      if (logisticsResponse.data?.success) {
        // Update state to show shipping is prepared
        setIsShippingPrepared(true);
        setShippingInfo({
          logisticsId: logisticsResponse.data.logisticsId,
          paymentNo: logisticsResponse.data.paymentNo,
          validationNo: logisticsResponse.data.validationNo
        });

        enqueueSnackbar('Shipping prepared successfully', { variant: 'success' });

        // Call optional callback if provided
        if (onShippingPrepared) {
          onShippingPrepared(logisticsResponse.data);
        }
      } else {
        throw new Error(logisticsResponse.data?.message || 'Failed to create logistics order');
      }

    } catch (error) {
      console.error('Shipping preparation error:', error);

      let errorMessage = 'Failed to prepare shipping';
      if (axios.isAxiosError(error) && error.response) {
        errorMessage = error.response.data?.message || error.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // If no order, return null
  if (!order) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <ShippingIcon />
            Prepare Shipping for Order #{order.orderNumber}
            {/* Payment Status Indicator */}
            <Chip
              label={isPaid ? 'Paid' : 'Unpaid'}
              color={isPaid ? 'success' : 'default'}
              size="small"
              sx={{ ml: 1 }}
              icon={isPaid ? <CheckCircleIcon /> : <ErrorIcon />}
            />

            {/* Shipping Status Indicator */}
            {isShippingPrepared && (
              <Chip
                label="Shipping Ready"
                color="info"
                size="small"
                sx={{ ml: 1 }}
                icon={<CheckCircleIcon />}
              />
            )}
          </Box>
          <Tooltip title="Close">
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>
      <DialogContent>
        {/* Information alert about ECPay shipping */}
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>台灣超商取貨設定</AlertTitle>
          請確認收件人姓名、電話與超商門市資訊正確無誤。姓名限制10個字元，請使用有效的手機號碼。
        </Alert>

        {/* Reminder: if not reshipping missing items later, mark the order shipped when done */}
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>出貨後提醒</AlertTitle>
          若此訂單有缺件／斷貨商品，且之後<strong>不會另外補寄</strong>，出貨完成後請記得回到訂單點「<strong>已出貨</strong>」，才會正確標記訂單完成。
        </Alert>

        {/* 斷貨扣款提示 — goods amount already excludes out-of-stock items */}
        {hasOOS && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>此訂單有 {oosCount} 件斷貨商品</AlertTitle>
            下方「商品金額 / 代收金額」已自動扣除斷貨商品 (−NT${oosDeduction.toLocaleString()})，
            為實際應付金額。斷貨商品在清單中以刪除線標示，不列入計算。
          </Alert>
        )}

        {/* Loading indicator during API fetch */}
        {fetchingInfo && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <CircularProgress size={24} />
            <Typography sx={{ ml: 2 }}>Fetching store information from Shopify...</Typography>
          </Box>
        )}

        {/* Store Information Display (similar to Streamlit) */}
        {!fetchingInfo && logisticsInfo.cvs_store_id && (
          <Paper sx={{ p: 2, mb: 3, bgcolor: '#f8f9fa' }}>
            <Typography variant="h6" gutterBottom>
              <StoreIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Convenience Store Information
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2">Store Type</Typography>
                <Typography>
                  {logisticsInfo.cvs_company ||
                    (logisticsInfo.logistics_subtype === 'UNIMARTC2C' ? '7-ELEVEN' :
                      logisticsInfo.logistics_subtype === 'FAMIC2C' ? 'Family Mart' : 'Unknown')}
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2">Store ID</Typography>
                <Typography>{logisticsInfo.cvs_store_id}</Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2">Store Name</Typography>
                <Typography>{logisticsInfo.store_name || 'N/A'}</Typography>
              </Grid>

              {logisticsInfo.store_address && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2">
                    <LocationIcon sx={{ mr: 0.5, verticalAlign: 'middle', fontSize: '1rem' }} />
                    Address
                  </Typography>
                  <Typography>
                    {logisticsInfo.store_address}
                    {logisticsInfo.store_city ? `, ${logisticsInfo.store_city}` : ''}
                    {logisticsInfo.store_zip ? ` ${logisticsInfo.store_zip}` : ''}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>
        )}

        {/* Shipping Information (if already prepared) */}
        {isShippingPrepared && shippingInfo && (
          <Paper sx={{ p: 2, mb: 3, bgcolor: '#e8f5e9' }}>
            <Typography variant="h6" gutterBottom color="success.main">
              <CheckCircleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Shipping Information
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Logistics ID</Typography>
                <Typography>{shippingInfo.logisticsId || 'N/A'}</Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Payment Number</Typography>
                <Typography fontWeight="bold">{shippingInfo.paymentNo || 'N/A'}</Typography>
              </Grid>

              {shippingInfo.validationNo && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Validation Number</Typography>
                  <Typography fontWeight="bold">{shippingInfo.validationNo}</Typography>
                </Grid>
              )}

              <Grid item xs={12}>
                <Box display="flex" justifyContent="center" mt={1}>
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<PrintIcon />}
                    onClick={handlePrintShippingDocument}
                    disabled={printing}
                  >
                    {printing ? 'Opening...' : 'Print Shipping Document'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        )}

        <Grid container spacing={3}>
          {/* Order Details Section */}
          <Grid item xs={12}>
            <Typography variant="h6">Order Information</Typography>
            <Grid container spacing={2}>
              {/* Customer Details */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Customer Name"
                  name="customerName"
                  value={shippingDetails.customerName}
                  onChange={handleInputChange}
                  inputProps={{ maxLength: 10 }}
                  required
                  error={!shippingDetails.customerName}
                  helperText={!shippingDetails.customerName ? 'Customer name is required (max 10 chars)' : ''}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Customer Phone"
                  name="customerPhone"
                  value={shippingDetails.customerPhone}
                  onChange={handleInputChange}
                  placeholder="09xxxxxxxx"
                  required
                  error={!shippingDetails.customerPhone}
                  helperText={!shippingDetails.customerPhone ? 'Valid Taiwan mobile number required' : ''}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Customer Email"
                  name="customerEmail"
                  value={shippingDetails.customerEmail}
                  onChange={handleInputChange}
                  type="email"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Goods Amount (TWD)"
                  name="totalAmount"
                  value={shippingDetails.totalAmount}
                  onChange={handleInputChange}
                  type="number"
                  required
                  error={!shippingDetails.totalAmount || shippingDetails.totalAmount <= 0}
                  helperText={!shippingDetails.totalAmount || shippingDetails.totalAmount <= 0 ? 'Goods amount must be greater than 0' : 'Amount for ECPay logistics'}
                  InputProps={{
                    inputProps: { min: 1, step: 1 }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography>
                  <strong>Total Items:</strong> {order.totalItems}
                </Typography>
                <Typography>
                  <strong>Order Date:</strong> {order.createdAt}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography>
                  <strong>Shipping Address:</strong> {order.shippingAddress}
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          {/* Convenience Store Details */}
          <Grid item xs={12}>
            <Typography variant="h6">Convenient Store Details</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required error={!shippingDetails.logistics_subtype}>
                  <InputLabel>Logistics Type</InputLabel>
                  <Select
                    name="logistics_subtype"
                    value={shippingDetails.logistics_subtype}
                    label="Logistics Type"
                    onChange={handleSelectChange}
                  >
                    <MenuItem value="UNIMARTC2C">7-ELEVEN</MenuItem>
                    <MenuItem value="FAMIC2C">全家 Family Mart</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="CVS Store ID"
                  name="cvs_store_id"
                  value={shippingDetails.cvs_store_id}
                  onChange={handleInputChange}
                  placeholder="Enter store ID"
                  required
                  error={!shippingDetails.cvs_store_id}
                  helperText={!shippingDetails.cvs_store_id ? 'Store ID is required' : ''}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Store Name"
                  name="store_name"
                  value={shippingDetails.store_name}
                  onChange={handleInputChange}
                  placeholder="Enter store name (optional)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Payment Collection</InputLabel>
                  <Select
                    name="isCollection"
                    value={shippingDetails.isCollection}
                    label="Payment Collection"
                    onChange={handleSelectChange}
                  >
                    <MenuItem value="N">No Payment Collection</MenuItem>
                    <MenuItem value="Y">Collect Payment on Delivery</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Goods Description"
                  name="itemsDescription"
                  value={shippingDetails.itemsDescription}
                  onChange={handleInputChange}
                  inputProps={{ maxLength: 25 }}
                  helperText="Max 25 characters, no special characters"
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Order Items Section - NEW */}
          <Paper sx={{ p: 2, mb: 3, bgcolor: '#f8f9fa' }}>
            <Typography variant="h6" gutterBottom>
              <InfoIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Order Items
            </Typography>

            <Grid container spacing={1}>
              {/* Table Header */}
              <Grid item xs={12}>
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 120px 80px 100px 100px',
                  gap: 1,
                  fontWeight: 'bold',
                  borderBottom: '1px solid #ddd',
                  pb: 1,
                  mb: 1
                }}>
                  <Typography variant="subtitle2">Product</Typography>
                  <Typography variant="subtitle2" align="center">Qty</Typography>
                  <Typography variant="subtitle2" align="right">Unit Price</Typography>
                  <Typography variant="subtitle2" align="right">Discount</Typography>
                  <Typography variant="subtitle2" align="right">Total</Typography>
                </Box>
              </Grid>

              {/* Order Items */}
              {order?.items?.map((item: any, index: number) => {
                const itemKey = item.id || String(index);
                // 贈品 lines are NT$0 — no price, no discount share
                const isGift = item.is_gift === true || item.isGift === true;
                const unitPrice = isGift ? 0 : parseFloat(item.price || 0);
                const origQuantity = parseInt(item.quantity || 0);
                const adjustedQty = itemQtyAdjustments[itemKey] !== undefined ? itemQtyAdjustments[itemKey] : origQuantity;
                const subtotal = unitPrice * adjustedQty;

                // Use total_discount from database, proportional to adjusted qty
                const origDiscount = isGift ? 0 : parseFloat(item.total_discount || 0);
                const discountPerUnit = origQuantity > 0 ? origDiscount / origQuantity : 0;
                const itemDiscounts = discountPerUnit * adjustedQty;

                // Apply order-level promo discount proportionally (gift lines excluded)
                const promoDiscount = parseFloat(order?.discountAmount || 0);
                const allItemsTotal = (order?.items || []).reduce((s: number, it: any) => (it.is_gift || it.isGift) ? s : s + parseFloat(it.price || 0) * parseInt(it.quantity || 0), 0);
                const promoRatio = promoDiscount > 0 && allItemsTotal > 0 ? promoDiscount / allItemsTotal : 0;
                const itemPromoDiscount = Math.round(unitPrice * adjustedQty * promoRatio);

                let itemTotal = subtotal - itemDiscounts - itemPromoDiscount;
                let lineDiscount = itemDiscounts + itemPromoDiscount;
                if (usesPaidShares && !isGift) {
                  // Same paid share the collected amount is built from.
                  itemTotal = paidShareOf(item, adjustedQty);
                  lineDiscount = Math.max(0, subtotal - itemTotal);
                }
                const isAdjusted = adjustedQty !== origQuantity;
                const isOOS = item.outOfStock === true;
                // The paid share removed from the goods amount for this 斷貨 line.
                const oosLineTotal = Math.round(parseFloat(item.paidLineTotal || 0));
                const dimmed = isOOS || adjustedQty === 0;

                return (
                  <Grid item xs={12} key={itemKey}>
                    <Box sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 120px 80px 100px 100px',
                      gap: 1,
                      alignItems: 'center',
                      py: 1,
                      borderBottom: '1px solid #eee',
                      bgcolor: isOOS ? 'rgba(230,81,0,0.08)' : adjustedQty === 0 ? 'rgba(244,67,54,0.05)' : isAdjusted ? 'rgba(255,152,0,0.05)' : 'inherit',
                      opacity: dimmed ? 0.6 : 1
                    }}>
                      {/* Product Name */}
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, textDecoration: dimmed ? 'line-through' : 'none' }}>
                            {item.productName || item.title}
                          </Typography>
                          {isOOS && (
                            <Chip label="斷貨" size="small" color="warning"
                              sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700 }} />
                          )}
                          {isGift && (
                            <Chip label="贈品" size="small" color="success"
                              sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700 }} />
                          )}
                        </Box>
                        {((item.itemSpec && item.itemSpec !== 'Default') ||
                          (item.variant_title && item.variant_title !== 'Default Title')) && (
                            <Typography variant="caption" color="text.secondary">
                              {item.itemSpec || item.variant_title}
                            </Typography>
                          )}
                        {item.sku && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            SKU: {item.sku}
                          </Typography>
                        )}
                      </Box>

                      {/* Quantity — +/- controls for in-stock items; 斷貨 items are fixed-excluded */}
                      {isOOS ? (
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#9e9e9e', textDecoration: 'line-through' }}>
                            {origQuantity}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#e65100', fontSize: '0.65rem', lineHeight: 1 }}>
                            不出貨
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25 }}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setItemQtyAdjustments(prev => ({
                                ...prev,
                                [itemKey]: Math.max(0, (prev[itemKey] !== undefined ? prev[itemKey] : origQuantity) - 1)
                              }));
                            }}
                            disabled={adjustedQty <= 0}
                            sx={{ p: 0.25, color: '#f44336', '&:hover': { bgcolor: 'rgba(244,67,54,0.1)' } }}
                          >
                            <RemoveIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                          <Box sx={{ textAlign: 'center', minWidth: 28 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: isAdjusted ? '#f57c00' : 'inherit' }}>
                              {adjustedQty}
                            </Typography>
                            {isAdjusted && (
                              <Typography variant="caption" sx={{ color: '#9e9e9e', textDecoration: 'line-through', fontSize: '0.65rem', lineHeight: 1 }}>
                                {origQuantity}
                              </Typography>
                            )}
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setItemQtyAdjustments(prev => ({
                                ...prev,
                                [itemKey]: Math.min(origQuantity, (prev[itemKey] !== undefined ? prev[itemKey] : origQuantity) + 1)
                              }));
                            }}
                            disabled={adjustedQty >= origQuantity}
                            sx={{ p: 0.25, color: '#4caf50', '&:hover': { bgcolor: 'rgba(76,175,80,0.1)' } }}
                          >
                            <AddIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      )}

                      {/* Unit Price */}
                      <Typography variant="body2" align="right" sx={{ textDecoration: dimmed ? 'line-through' : 'none' }}>
                        ${unitPrice.toFixed(2)}
                      </Typography>

                      {/* Discount */}
                      <Typography
                        variant="body2"
                        align="right"
                        color={!isOOS && lineDiscount > 0 ? "error.main" : "text.secondary"}
                      >
                        {isOOS ? '—' : lineDiscount > 0 ? `-$${lineDiscount.toFixed(2)}` : '-'}
                      </Typography>

                      {/* Item Total — 斷貨 lines show the amount removed (not collected) */}
                      {isOOS ? (
                        <Typography variant="body2" align="right" sx={{ fontWeight: 600, color: '#e65100' }}>
                          −${oosLineTotal.toFixed(2)}
                        </Typography>
                      ) : (
                        <Typography variant="body2" align="right" sx={{ fontWeight: 500, color: isAdjusted ? '#f57c00' : 'inherit' }}>
                          ${itemTotal.toFixed(2)}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                );
              })}

              {/* Order Summary */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 120px 80px 100px 100px',
                  gap: 1,
                  alignItems: 'center',
                  fontWeight: 'bold'
                }}>
                  <Typography variant="subtitle2">
                    Order Total
                    {Object.keys(itemQtyAdjustments).length > 0 && (
                      <Chip label="已調整" size="small" color="warning" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />
                    )}
                  </Typography>
                  <Typography variant="subtitle2" align="center">
                    {(() => {
                      const items = order?.items || [];
                      let totalQty = 0;
                      items.forEach((item: any, idx: number) => {
                        if (item.outOfStock) return; // 斷貨 items aren't shipped
                        const k = item.id || String(idx);
                        totalQty += itemQtyAdjustments[k] !== undefined ? itemQtyAdjustments[k] : parseInt(item.quantity || 0);
                      });
                      return totalQty;
                    })()}
                  </Typography>
                  <Typography variant="subtitle2" align="right">-</Typography>
                  <Typography variant="subtitle2" align="right" color="error.main">
                    -${(parseFloat(order?.original?.total_discounts || 0) + parseFloat(order?.discountAmount || 0)).toFixed(2)}
                  </Typography>
                  <Typography variant="subtitle2" align="right" color="primary.main">
                    ${shippingDetails.totalAmount.toFixed(2)}
                  </Typography>
                </Box>

                {/* 斷貨扣款 reconciliation — makes the deducted total add up visibly */}
                {(hasOOS || (usesPaidShares && (storeCreditUsed > 0 || qtyRemovedAmount > 0))) && (
                  <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#fff8e1', borderRadius: 1, border: '1px solid #ffe082' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">商品原始金額</Typography>
                      <Typography variant="body2" color="text.secondary">${originalGoods.toLocaleString()}</Typography>
                    </Box>
                    {hasOOS && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ color: '#e65100', fontWeight: 600 }}>斷貨扣款（{oosCount} 件）</Typography>
                        <Typography variant="body2" sx={{ color: '#e65100', fontWeight: 600 }}>−${oosDeduction.toLocaleString()}</Typography>
                      </Box>
                    )}
                    {hasOOS && order?.oosKeepsTier && (
                      <Typography variant="caption" sx={{ display: 'block', color: '#92400e', mb: 0.5 }}>
                        扣除斷貨後仍達滿額門檻 — 斷貨商品以原價扣除，客人保留滿額折扣
                      </Typography>
                    )}
                    {storeCreditUsed > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">購物金折抵</Typography>
                        <Typography variant="body2" color="text.secondary">−${storeCreditUsed.toLocaleString()}</Typography>
                      </Box>
                    )}
                    {qtyRemovedAmount > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ color: '#f57c00', fontWeight: 600 }}>數量調整扣款</Typography>
                        <Typography variant="body2" sx={{ color: '#f57c00', fontWeight: 600 }}>−${qtyRemovedAmount.toLocaleString()}</Typography>
                      </Box>
                    )}
                    <Divider sx={{ my: 0.5 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>實際商品金額</Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1976d2' }}>
                        ${Math.round(shippingDetails.totalAmount).toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {Object.keys(itemQtyAdjustments).length > 0 && (
                  <Box sx={{ mt: 1, textAlign: 'right' }}>
                    <Button
                      size="small"
                      variant="text"
                      color="warning"
                      onClick={() => {
                        setItemQtyAdjustments({});
                        setShippingDetails(prev => ({ ...prev, totalAmount: calculateOrderTotal(order) }));
                      }}
                    >
                      重置數量
                    </Button>
                  </Box>
                )}
              </Grid>
            </Grid>
          </Paper>

          {/* Sender Details */}
          <Grid item xs={12}>
            <Typography variant="h6">Sender Information</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Sender Name"
                  name="senderName"
                  value={shippingDetails.senderName}
                  onChange={handleInputChange}
                  required
                  error={!shippingDetails.senderName}
                  helperText={!shippingDetails.senderName ? 'Sender name is required' : ''}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Sender Phone"
                  name="senderPhone"
                  value={shippingDetails.senderPhone}
                  onChange={handleInputChange}
                  required
                  error={!shippingDetails.senderPhone}
                  helperText={!shippingDetails.senderPhone ? 'Sender phone is required' : ''}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Sender Address"
                  name="senderAddress"
                  value={shippingDetails.senderAddress}
                  onChange={handleInputChange}
                  placeholder="Optional shipping address"
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>

        {/* Always show and enable the Print Button */}
        <Button
          onClick={handlePrintShippingDocument}
          color="secondary"
          variant="outlined"
          startIcon={<PrintIcon />}
          disabled={printing} // Only disable during printing
          sx={{ mr: 1 }}  // Add margin to separate the buttons
        >
          {printing ? 'Opening...' : '列印'}
        </Button>

        {/* Prepare Shipping Button - Not visible if already prepared */}
        {!isShippingPrepared && (
          <Button
            onClick={handleSubmitShipping}
            color="primary"
            variant="contained"
            disabled={loading ||
              fetchingInfo ||
              !shippingDetails.senderName ||
              !shippingDetails.senderPhone ||
              !shippingDetails.customerName ||
              !shippingDetails.customerPhone ||
              !shippingDetails.cvs_store_id}
            startIcon={loading || fetchingInfo ? <CircularProgress size={16} /> : <ShippingIcon />}
          >
            {loading ? 'Processing...' : (fetchingInfo ? 'Fetching Info...' : '建立物流單')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ShippingPreparationModal;